import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormMessage } from "@/components/ui/form-message";
import { Input } from "@/components/ui/input";
import type { CreateTestAccountInput } from "@/features/admin/admin.types";
import { validateCreateTestAccountInput } from "@/features/admin/adminValidation";

type CreateTestAccountDialogProps = {
  busy?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: CreateTestAccountInput) => void;
  open: boolean;
};

const defaultState: CreateTestAccountInput = {
  email: "",
  firstName: "",
  lastName: "",
  password: "password123",
  tier: "crafter",
};

export function CreateTestAccountDialog({
  busy = false,
  onOpenChange,
  onSubmit,
  open,
}: CreateTestAccountDialogProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateTestAccountInput>(defaultState);
  const [errors, setErrors] = useState<Partial<Record<"email" | "firstName" | "lastName" | "password", string>>>({});

  useEffect(() => {
    if (open) {
      setForm(defaultState);
      setErrors({});
    }
  }, [open]);

  function handleSubmit() {
    const validationErrors = validateCreateTestAccountInput(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    onSubmit({
      ...form,
      email: form.email.trim().toLowerCase(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("adminWorkspace.dialogs.createTestAccount.title")}</DialogTitle>
          <DialogDescription>
            {t("adminWorkspace.dialogs.createTestAccount.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Input placeholder={t("auth.fields.email")} value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} />
            {errors.email ? <FormMessage>{t(errors.email)}</FormMessage> : null}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Input placeholder={t("auth.fields.firstName")} value={form.firstName} onChange={(event) => setForm((current) => ({ ...current, firstName: event.target.value }))} />
              {errors.firstName ? <FormMessage>{t(errors.firstName)}</FormMessage> : null}
            </div>
            <div>
              <Input placeholder={t("auth.fields.lastName")} value={form.lastName} onChange={(event) => setForm((current) => ({ ...current, lastName: event.target.value }))} />
              {errors.lastName ? <FormMessage>{t(errors.lastName)}</FormMessage> : null}
            </div>
          </div>
          <div>
            <Input placeholder={t("auth.fields.password")} value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} />
            {errors.password ? <FormMessage>{t(errors.password)}</FormMessage> : null}
          </div>
          <select
            className="w-full rounded-3xl border border-ink/10 bg-white/80 px-4 py-3 text-sm text-ink shadow-sm outline-none transition focus:border-clay/50 focus:ring-2 focus:ring-clay/20"
            value={form.tier}
            onChange={(event) => setForm((current) => ({ ...current, tier: event.target.value as CreateTestAccountInput["tier"] }))}
          >
            <option value="free">{t("common.tiers.free")}</option>
            <option value="crafter">{t("common.tiers.crafter")}</option>
            <option value="author">{t("common.tiers.author")}</option>
          </select>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button disabled={busy} type="button" onClick={handleSubmit}>
            {busy ? t("adminWorkspace.dialogs.createTestAccount.creating") : t("adminWorkspace.dialogs.createTestAccount.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
